import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { showToast } from '../components/common/Toast';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('advokat_token'));
  const [loading, setLoading] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const refreshSubscription = useCallback(async () => {
    const savedToken = localStorage.getItem('advokat_token');
    if (!savedToken) {
      setSubscriptionStatus(null);
      return null;
    }
    try {
      const res = await api.get('/subscriptions/my-status');
      if (res?.success) {
        setSubscriptionStatus(res);
        return res;
      }
    } catch {
      setSubscriptionStatus(null);
    }
    return null;
  }, []);

  const refreshUser = useCallback(async () => {
    const savedToken = localStorage.getItem('advokat_token');
    if (!savedToken) return null;

    try {
      const res = await api.get('/auth/me');
      if (res?.success && res?.user) {
        setUser(res.user);
        if (typeof res.user.unreadNotifications === 'number') {
          setUnreadNotifications(res.user.unreadNotifications);
        }
        refreshSubscription();
        return res.user;
      }
    } catch (err) {
      console.warn('Failed to refresh user profile:', err.message);
    }
    return null;
  }, [refreshSubscription]);

  useEffect(() => {
    let isMounted = true;

    // Safety fallback: never keep loading stuck forever!
    const safetyTimer = setTimeout(() => {
      if (isMounted) setLoading(false);
    }, 3000);

    async function loadUser() {
      const savedToken = localStorage.getItem('advokat_token');
      if (savedToken) {
        try {
          const res = await api.get('/auth/me');
          if (isMounted && res?.success && res?.user) {
            setUser(res.user);
            if (typeof res.user.unreadNotifications === 'number') {
              setUnreadNotifications(res.user.unreadNotifications);
            }
            refreshSubscription();
          } else if (isMounted) {
            localStorage.removeItem('advokat_token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        } catch {
          if (isMounted) {
            localStorage.removeItem('advokat_token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          }
        }
      }
      if (isMounted) setLoading(false);
      clearTimeout(safetyTimer);
    }

    loadUser();

    const handleUnauthorized = (e) => {
      const savedToken = localStorage.getItem('advokat_token');
      if (savedToken) {
        localStorage.removeItem('advokat_token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        const msg = e.detail?.message || 'Ruxsatingiz ma’muriyat tomonidan bekor qilindi yoki sessiya muddati tugadi.';
        showToast(msg, 'error');
        if (window.location.pathname.startsWith('/admin')) {
          if (window.location.pathname !== '/admin/login') {
            window.location.href = '/admin/login';
          }
        } else {
          setTimeout(() => {
            window.location.href = '/';
          }, 350);
        }
      }
    };
    window.addEventListener('advokat:unauthorized', handleUnauthorized);
    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
      window.removeEventListener('advokat:unauthorized', handleUnauthorized);
    };
  }, []);

  // Periodic poll for session validity & notifications if logged in
  useEffect(() => {
    if (!user) return;
    const isStaffAdmin = user.role === 'SUPER_ADMIN' || user.role === 'ADMIN';
    // For clients and lawyers, poll every 5 seconds so revoked access kicks them out immediately!
    const pollInterval = isStaffAdmin ? 25000 : 5000;
    const interval = setInterval(() => {
      api.get('/notifications/my')
        .then((res) => {
          if (res?.success && typeof res.unreadCount === 'number') {
            setUnreadNotifications(res.unreadCount);
          }
        })
        .catch(() => {});
    }, pollInterval);
    return () => clearInterval(interval);
  }, [user]);

  const login = async (loginIdentifier, password, step2Password = undefined) => {
    const payload = { login: loginIdentifier, password };
    if (step2Password !== undefined) {
      payload.step2Password = step2Password;
    }
    const res = await api.post('/auth/login', payload);
    if (res?.requiresTwoFactor) {
      return { requiresTwoFactor: true, message: res.message };
    }
    if (res?.success && res?.token) {
      localStorage.setItem('advokat_token', res.token);
      setToken(res.token);
      setUser(res.user);
      if (typeof res.user.unreadNotifications === 'number') {
        setUnreadNotifications(res.user.unreadNotifications);
      }
      return res.user;
    }
    throw new Error(res?.message || 'Kirishda xatolik yuz berdi');
  };

  const register = async ({ fullName, username, phone, password, region, district, email }) => {
    const res = await api.post('/auth/register', {
      fullName,
      username,
      phone,
      password,
      region,
      district,
      email: email || undefined,
    });
    if (res?.success && res?.token) {
      localStorage.setItem('advokat_token', res.token);
      setToken(res.token);
      setUser(res.user);
      setUnreadNotifications(0);
      return res.user;
    }
    throw new Error(res?.message || 'Ro‘yxatdan o‘tishda xatolik yuz berdi');
  };

  const updateProfile = async (profileData) => {
    const res = await api.put('/auth/profile', profileData);
    if (res?.success && res?.user) {
      setUser((prev) => ({ ...prev, ...res.user }));
      return res.user;
    }
    throw new Error(res?.message || 'Profilni yangilashda xatolik yuz berdi');
  };

  const logout = () => {
    localStorage.removeItem('advokat_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setUnreadNotifications(0);
    if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
      window.location.href = '/admin/login';
    }
  };

  const isSubscribed = Boolean(
    (user && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN')) ||
    (subscriptionStatus && (subscriptionStatus.isFreeExempt || subscriptionStatus.isActive))
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: Boolean(user),
        isClient: Boolean(user?.isClient),
        isAdmin: Boolean(user && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN')),
        isLawyer: Boolean(user && user.role === 'LAWYER'),
        isSubscribed,
        subscriptionStatus,
        refreshSubscription,
        loading,
        unreadNotifications,
        setUnreadNotifications,
        login,
        register,
        updateProfile,
        refreshUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
